import {AiAgent, AiScenario, AiScorecardDetails} from './top-level-statistic.model';

interface TimeDuration {
  minutes: number;
  seconds: number;
  attempts?: number;
}

export interface BaseLearnerStatisticInterface {
  firstScore: number;
  bestScore: number;
  avgScore: number;
  totalScore: number;
  totalAttempts: number;
  totalDuration: number;
  attemptToMastery: number;
  timeToMastery: number;
  timeToMasteryText: string;
  improvement: number;
  performanceImprovement: number;
  improvementText: string;
  consistencyGap: number;
}

export interface ManagerLearnerStatisticInterface extends BaseLearnerStatisticInterface {
  userId: string;
  delegateId: number;
  name: string;
  scenarioCompleted: number;
  scenarioCompletedPercent: number;
  scenarioCount: number;
  scenarios: AiScenario[];
  scenarioIds: number[];
  details: AiScorecardDetails[];
}

export interface UserLearnerStatisticInterface extends BaseLearnerStatisticInterface {
  learnerId: string;
  scenarioId: number;
  scenarioTitle: string;
  firstScore: number;
  bestScorePercent: number;
  aiAgent: AiAgent;
}

export class LearnerStatistic implements ManagerLearnerStatisticInterface {
  public readonly userId: string;
  public readonly delegateId: number;
  public readonly name: string;

  public readonly scenarioCompleted: number;
  public readonly scenarioCompletedPercent: number;
  public readonly scenarioCount: number;
  public readonly scenarios: AiScenario[];
  public readonly scenarioIds: number[];
  public readonly agents: AiAgent[];

  public readonly agentIds: number[];
  public readonly avgScore: number;
  public readonly firstScore: number;
  public readonly bestScore: number;
  public readonly attemptToMastery: number;
  public readonly totalScore: number;
  public readonly totalDuration: number;
  public readonly totalAttempts: number;
  public readonly timeToMastery: number;
  public readonly timeToMasteryText: string;
  public readonly improvement: number;
  public readonly performanceImprovement: number;
  public readonly improvementText: string;
  public readonly consistencyGap: number;
  public readonly details: AiScorecardDetails[];

  public constructor(
    userId: string,
    delegateId: number,
    fName: string,
    lName: string,
    scenarios: AiScenario[],
    details: AiScorecardDetails[]
  ) {
    this.userId = userId;
    this.delegateId = delegateId;
    this.name = `${fName} ${lName}`;
    this.scenarioIds = scenarios.map(s => s.id);
    this.scenarioCount = scenarios.length;
    this.agents = scenarios.reduce((acc, scenario) => [...acc, ...scenario.AiScenarioAgents], []);
    this.agentIds = this.agents.map(agent => agent.id);
    this.details = details.filter(d => this.agentIds.includes(d.aiAgent.id));

    this.scenarioCompleted = scenarios.reduce((completedScenarios, scenario) => {
      const completedAgentsByScenario = scenario.AiScenarioAgents.reduce((completedAgents, agent) => {
        const attempt = this.details.find(d => d.aiAgent.id === agent.id);
        if (attempt) {
          return attempt.bestAttemptScore >= agent.threshold ? completedAgents + 1 : completedAgents;
        }
        return completedAgents;
      }, 0);

      if (completedAgentsByScenario === scenario.AiScenarioAgents.length) {
        completedScenarios.push(scenario);
      }
      return completedScenarios;
    }, this.scenarioIds = []).length;

    this.scenarioCompletedPercent = this.getPercentage(this.scenarioCompleted, this.scenarioCount);

    this.firstScore = this.calcAverage(this.details.map(d => d.firstAttemptScore));
    this.avgScore = this.calcAverage(this.details.map(d => d.totalAttempts ? d.totalScore / d.totalAttempts : 0));
    this.bestScore = this.calcAverage(this.details.map(d => d.bestAttemptScore));

    this.attemptToMastery = this.calcAverage(this.details.map(d => d.thresholdAttempt));
    this.timeToMastery = this.calcAverage(this.details.map(d => d.thresholdDuration)) / 1000 / 60;
    this.timeToMasteryText = this.formatTimeToMastery(this.details);

    this.consistencyGap = this.bestScore - this.avgScore;

    this.improvement = this.avgScore - this.firstScore;
    this.improvementText = this.getImprovementText(this.improvement);

    this.performanceImprovement = this.bestScore - this.firstScore;

    this.totalDuration = this.calcAverage(this.details.map(d => d.totalDuration)) / 1000 / 60;
    this.totalAttempts = this.details.map(d => d.totalAttempts).reduce((acc, d) => acc + d, 0);
    this.totalScore = this.details.map(d => d.totalScore).reduce((acc, d) => acc + d, 0);
  }

  private getPercentage(value: number, max: number): number {
    return max === 0 ? 0 : (value / max) * 100;
  }

  private calcAverage(values: number[]): number {
    if (!values.length) {return 0;}
    return values.reduce((acc, v) => acc + v, 0) / values.length;
  }

  private formatTimeToMastery(details: AiScorecardDetails[]): string {
    const avgDuration = this.getAverageDuration(details);
    return this.formatDurationText(avgDuration);
  }

  private getAverageDuration(details: AiScorecardDetails[]): TimeDuration {
    if (!details.length) return null;

    const durationsSum = details.reduce((acc, d) => acc + d.thresholdDuration, 0);
    if (!durationsSum) return null;

    const avgDurationMs = durationsSum / details.length;
    const totalSeconds = Math.floor(avgDurationMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return { minutes, seconds };
  }

  private formatDurationText(duration: TimeDuration): string {
    if (!duration) return '-';

    return `${duration.minutes} min ${String(duration.seconds).padStart(2, '0')} sec`;
  }

  private getImprovementText(improvement: number): string {
    if (improvement < 20) {return 'Needs Activation';}
    if (improvement < 40) {return 'Building Awareness';}
    if (improvement < 60) {return 'Earning Competence';}
    if (improvement < 80) {return 'Developing Strength';}
    return 'Performance Ready';
  }
}

export class UserLearnerStatistic implements UserLearnerStatisticInterface {
  public readonly learnerId: string;
  public readonly scenarioId: number;
  public readonly scenarioTitle: string;
  public readonly firstScore: number;
  public readonly bestScore: number;
  public readonly avgScore: number;
  public readonly bestScorePercent: number;
  public readonly totalScore: number;
  public readonly totalAttempts: number;
  public readonly totalDuration: number;
  public readonly attemptToMastery: number;
  public readonly timeToMastery: number;
  public readonly timeToMasteryText: string;
  public readonly improvement: number;
  public readonly performanceImprovement: number;
  public readonly improvementText: string;
  public readonly consistencyGap: number;
  public readonly aiAgent: AiAgent;

  constructor(learnerId: string,
              scenarioId: number,
              scenarioTitle: string,
              details: AiScorecardDetails
             ) {
    this.learnerId = learnerId;
    this.scenarioId = scenarioId;
    this.scenarioTitle = scenarioTitle;

    const best = details.bestAttemptScore || 0;
    const first = details.firstAttemptScore || 0;
    const average = details.totalAttempts ? details.totalScore / details.totalAttempts : 0;

    this.bestScore = best;
    this.bestScorePercent = best;
    this.firstScore = first;
    this.avgScore = average;

    this.totalAttempts = details.totalAttempts || 0;
    this.totalDuration = details.totalDuration / 1000 / 60 || 0;
    this.attemptToMastery = details.thresholdAttempt || 0;

    this.timeToMastery = details.thresholdDuration / 1000 / 60 || 0;
    this.timeToMasteryText = this.formatTimeToMastery(details);

    this.improvement = average - first;
    this.improvementText = this.getImprovementText(this.improvement);

    this.performanceImprovement = best - first;
    this.consistencyGap = best - average;
    this.aiAgent = details.aiAgent;
  }

  private formatTimeToMastery(details: AiScorecardDetails): string {
    const avgDuration = this.getAverageDuration(details);
    return this.formatDurationText(avgDuration);
  }

  private getAverageDuration(details: AiScorecardDetails): TimeDuration {
    if (!details) return null;

    const attempts = details.thresholdAttempt || 0;
    const minutes = Math.floor(details.thresholdDuration / 1000 / 60) || 0;
    const seconds = Math.floor(details.thresholdDuration / 1000 % 60) || 0;

    if (!attempts) {
      return null;
    }


    return { attempts, minutes, seconds };
  }

  private formatDurationText(duration: TimeDuration): string {
    if (!duration) return 'N/A';

    return `${duration.attempts} / ${duration.minutes} min ${String(duration.seconds).padStart(2, '0')} sec`;
  }

  private getImprovementText(improvement: number): string {
    if (improvement < 20) {return 'Needs Activation';}
    if (improvement < 40) {return 'Building Awareness';}
    if (improvement < 60) {return 'Earning Competence';}
    if (improvement < 80) {return 'Developing Strength';}
    return 'Performance Ready';
  }
}
